import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils';

const cardVariants = cva(
  'rounded-3xl border border-border/80 text-card-foreground shadow-hard-sm transition-[background-color,border-color]',
  {
    variants: {
      variant: {
        default: 'bg-card',
        pink: 'bg-riso-pink text-white',
        teal: 'bg-riso-teal text-white',
        amber: 'bg-riso-amber text-foreground',
        blue: 'bg-riso-blue text-white',
        rose: 'bg-riso-pink-light',
        sky: 'bg-riso-teal-light',
        sunshine: 'bg-riso-amber-light',
        ghost: 'border-transparent shadow-none',
        'accent-pink': 'bg-card border-riso-pink/20 shadow-hard-pink',
        'accent-teal': 'bg-card border-riso-teal/20 shadow-hard-teal',
        'accent-amber': 'bg-card border-riso-amber/20 shadow-hard-amber',
        'accent-blue': 'bg-card border-riso-blue/20 shadow-hard-blue',
      },
      static: {
        true: null,
        false: 'cursor-pointer',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        static: false,
        className: 'hover:border-riso-blue/35 hover:bg-muted/40',
      },
      {
        variant: 'pink',
        static: false,
        className: 'hover:border-white/50 hover:bg-riso-pink/90',
      },
      {
        variant: 'teal',
        static: false,
        className: 'hover:border-white/50 hover:bg-riso-teal/90',
      },
      {
        variant: 'amber',
        static: false,
        className: 'hover:border-foreground/30 hover:bg-riso-amber/90',
      },
      {
        variant: 'blue',
        static: false,
        className: 'hover:border-white/50 hover:bg-riso-blue/90',
      },
      {
        variant: 'rose',
        static: false,
        className: 'hover:border-riso-pink/35 hover:bg-riso-pink-light/70',
      },
      {
        variant: 'sky',
        static: false,
        className: 'hover:border-riso-teal/35 hover:bg-riso-teal-light/70',
      },
      {
        variant: 'sunshine',
        static: false,
        className: 'hover:border-riso-amber/35 hover:bg-riso-amber-light/70',
      },
      {
        variant: 'accent-pink',
        static: false,
        className: 'hover:border-riso-pink/45 hover:bg-riso-pink-light/30',
      },
      {
        variant: 'accent-teal',
        static: false,
        className: 'hover:border-riso-teal/45 hover:bg-riso-teal-light/30',
      },
      {
        variant: 'accent-amber',
        static: false,
        className: 'hover:border-riso-amber/45 hover:bg-riso-amber-light/30',
      },
      {
        variant: 'accent-blue',
        static: false,
        className: 'hover:border-riso-blue/45 hover:bg-riso-blue-light/30',
      },
    ],
    defaultVariants: {
      variant: 'default',
      static: false,
    },
  },
);

export type CardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants>;

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, static: isStatic, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, static: isStatic }), className)}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'font-display text-2xl font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'font-body text-sm leading-relaxed text-muted-foreground',
      className,
    )}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  cardVariants,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
